/**
 * Regen Config
 *
 * Passive health and mana regeneration. Tunable knobs DJ tweaks while
 * balancing danger vs. downtime.
 *
 * HP regen is combat-gated (plan 03, adopted 2026-10-07): none mid-fight,
 * then a percentage of max HP per second once the player has neither dealt
 * nor taken damage for `outOfCombatDelayMs`. Mana is unchanged (flat).
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface HealthRegenConfig {
  /** Flat HP restored per second while in combat (0 = none). */
  inCombatPerSecond: number;
  /** Time since the player last dealt or took damage before out-of-combat regen starts. */
  outOfCombatDelayMs: number;
  /** Fraction of max HP restored per second out of combat (0.012 ≈ 75 s from 10% to full). */
  outOfCombatPctPerSecond: number;
}

export interface RegenConfig {
  health: HealthRegenConfig;
  /** MP restored per second, in or out of combat. */
  manaPerSecond: number;
}

export const REGEN_DEFAULTS: RegenConfig = {
  health: {
    inCombatPerSecond: 0,
    outOfCombatDelayMs: 6000,
    outOfCombatPctPerSecond: 0.012,
  },
  manaPerSecond: 5,
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to REGEN_DEFAULTS.
 *
 * Examples:
 *   health: { ...REGEN_DEFAULTS.health, outOfCombatPctPerSecond: 0.5 }  // fast refill for ability iteration
 *   manaPerSecond: 999       // unlimited spell casting for QA
 */
export const REGEN_DEV_OVERRIDE: Partial<RegenConfig> = {};

export const REGEN: RegenConfig = {
  ...REGEN_DEFAULTS,
  ...REGEN_DEV_OVERRIDE,
};
